/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package forecasting

import kotlin.math.abs
import kotlin.math.pow
import kotlin.math.sqrt

/**
 * Forecasting service that coordinates algorithm selection and execution
 * Provides high-level API for financial time series forecasting
 */
class ForecastingService(
    private val config: ForecastingServiceConfig = ForecastingServiceConfig(),
) {
    private val algorithms = mutableMapOf<String, ForecastingAlgorithm>()

    init {
        // Register default algorithms
        registerAlgorithm("SimpleMovingAverage", SimpleMovingAverage(period = 5))
        registerAlgorithm("ExponentialWeightedMovingAverage", ExponentialWeightedMovingAverage(alpha = 0.3))
        registerAlgorithm("SimpleExponentialSmoothing", SimpleExponentialSmoothing(alpha = 0.3))
        registerAlgorithm("LinearTrendRegression", LinearTrendRegression())
    }

    /**
     * Get list of available algorithm names
     */
    fun getAvailableAlgorithms(): List<String> = algorithms.keys.toList()

    /**
     * Forecast using a specific algorithm by name
     */
    fun forecastWithAlgorithm(
        algorithmName: String,
        data: List<Double>,
        periods: Int,
    ): ForecastingResult {
        if (periods <= 0) {
            return ForecastingResult(
                algorithmName = "Invalid Request",
                forecastValues = emptyList(),
                confidence = 0.0,
                parameters = mapOf("error" to "Invalid periods: $periods"),
            )
        }

        val algorithm = algorithms[algorithmName]
        return if (algorithm != null) {
            algorithm.forecast(data, periods)
        } else {
            ForecastingResult(
                algorithmName = "Error: Unknown Algorithm",
                forecastValues = emptyList(),
                confidence = 0.0,
                parameters = mapOf("requested" to algorithmName, "available" to getAvailableAlgorithms()),
            )
        }
    }

    /**
     * Forecast using multiple algorithms
     */
    fun forecastWithMultipleAlgorithms(
        algorithmNames: List<String>,
        data: List<Double>,
        periods: Int,
    ): List<ForecastingResult> = algorithmNames.map { name -> forecastWithAlgorithm(name, data, periods) }

    /**
     * Automatically select and use the best algorithm for given data
     */
    fun forecastWithBestAlgorithm(
        data: List<Double>,
        periods: Int,
    ): ForecastingResult {
        if (data.isEmpty()) {
            return ForecastingResult(
                algorithmName = "No Data",
                forecastValues = emptyList(),
                confidence = 0.0,
                parameters = mapOf("error" to "Empty dataset"),
            )
        }

        val recommendation = recommendAlgorithm(data)
        return forecastWithAlgorithm(recommendation.algorithmName, data, periods)
    }

    /**
     * Recommend the best algorithm for given data characteristics
     */
    fun recommendAlgorithm(data: List<Double>): AlgorithmRecommendation {
        if (data.isEmpty()) {
            return AlgorithmRecommendation(
                algorithmName = "SimpleMovingAverage",
                confidence = 0.0,
                reason = "Default choice for empty data",
            )
        }

        if (data.size < 3) {
            return AlgorithmRecommendation(
                algorithmName = "SimpleExponentialSmoothing",
                confidence = 0.3,
                reason = "Simple exponential smoothing for insufficient data",
            )
        }

        // Analyze data characteristics
        val mean = data.average()
        val variance = data.map { (it - mean).pow(2) }.average()
        val stdDev = sqrt(variance)
        val coefficientOfVariation = if (mean != 0.0) stdDev / abs(mean) else 1.0

        // Check for trend
        val firstHalf = data.take(data.size / 2)
        val secondHalf = data.drop(data.size / 2)
        val trendStrength = abs(secondHalf.average() - firstHalf.average()) / stdDev

        return when {
            trendStrength > 1.0 ->
                AlgorithmRecommendation(
                    algorithmName = "LinearTrendRegression",
                    confidence = 0.8,
                    reason = "Strong trend detected (strength: ${(trendStrength * 100).toInt() / 100.0})",
                )
            coefficientOfVariation > 0.5 ->
                AlgorithmRecommendation(
                    algorithmName = "ExponentialWeightedMovingAverage",
                    confidence = 0.7,
                    reason = "High volatility detected (CV: ${(coefficientOfVariation * 100).toInt() / 100.0})",
                )
            coefficientOfVariation < 0.1 ->
                AlgorithmRecommendation(
                    algorithmName = "SimpleMovingAverage",
                    confidence = 0.9,
                    reason = "Stable data detected (CV: ${(coefficientOfVariation * 100).toInt() / 100.0})",
                )
            else ->
                AlgorithmRecommendation(
                    algorithmName = "SimpleExponentialSmoothing",
                    confidence = 0.7,
                    reason = "General-purpose choice for moderate volatility",
                )
        }
    }

    /**
     * Compare performance of all available algorithms
     */
    fun compareAlgorithms(
        data: List<Double>,
        periods: Int,
    ): List<ForecastingResult> =
        getAvailableAlgorithms()
            .map { algorithmName ->
                forecastWithAlgorithm(algorithmName, data, periods)
            }.sortedByDescending { it.confidence }

    /**
     * Benchmark algorithms against historical data
     */
    fun benchmarkAlgorithms(
        trainingData: List<Double>,
        testData: List<Double>,
    ): List<AlgorithmBenchmark> {
        if (trainingData.isEmpty() || testData.isEmpty()) {
            return emptyList()
        }

        return getAvailableAlgorithms()
            .map { algorithmName ->
                val algorithm = algorithms[algorithmName]!!
                val result = algorithm.forecast(trainingData, testData.size)

                // Calculate Mean Absolute Error
                val errors =
                    result.forecastValues.zip(testData).map { (predicted, actual) ->
                        abs(predicted - actual)
                    }
                val mae = if (errors.isNotEmpty()) errors.average() else Double.MAX_VALUE

                // Calculate Mean Absolute Percentage Error
                val percentageErrors =
                    result.forecastValues.zip(testData).map { (predicted, actual) ->
                        if (actual != 0.0) abs((predicted - actual) / actual) else 1.0
                    }
                val mape = if (percentageErrors.isNotEmpty()) percentageErrors.average() else 1.0

                AlgorithmBenchmark(
                    algorithmName = algorithmName,
                    meanAbsoluteError = mae,
                    meanAbsolutePercentageError = mape,
                    confidence = result.confidence,
                )
            }.sortedBy { it.meanAbsoluteError }
    }

    /**
     * Optimize parameters for a specific algorithm
     */
    fun optimizeAlgorithmParameters(
        algorithmName: String,
        data: List<Double>,
    ): OptimizationResult =
        when (algorithmName) {
            "SimpleMovingAverage" -> optimizeMovingAveragePeriod(data)
            "ExponentialWeightedMovingAverage" -> optimizeEWMAAlpha(data)
            "SimpleExponentialSmoothing" -> optimizeExponentialSmoothingAlpha(data)
            "LinearTrendRegression" -> OptimizationResult(emptyMap(), 0.8, 0)
            else ->
                OptimizationResult(
                    parameters = emptyMap(),
                    confidence = 0.0,
                    iterations = 0,
                )
        }

    /**
     * Register a custom algorithm
     */
    fun registerCustomAlgorithm(
        name: String,
        algorithm: ForecastingAlgorithm,
    ) {
        algorithms[name] = algorithm
    }

    private fun registerAlgorithm(
        name: String,
        algorithm: ForecastingAlgorithm,
    ) {
        algorithms[name] = algorithm
    }

    private fun optimizeMovingAveragePeriod(data: List<Double>): OptimizationResult {
        val maxPeriod = minOf(data.size / 2, 20)
        var bestPeriod = 5
        var bestError = Double.MAX_VALUE

        for (period in 2..maxPeriod) {
            val algorithm = SimpleMovingAverage(period)
            val trainingSize = (data.size * 0.8).toInt()
            val trainingData = data.take(trainingSize)
            val testData = data.drop(trainingSize)

            if (testData.isEmpty()) continue

            val result = algorithm.forecast(trainingData, testData.size)
            val errors = result.forecastValues.zip(testData).map { (pred, actual) -> abs(pred - actual) }
            val mae = errors.average()

            if (mae < bestError) {
                bestError = mae
                bestPeriod = period
            }
        }

        return OptimizationResult(
            parameters = mapOf("period" to bestPeriod),
            confidence = 1.0 / (1.0 + bestError),
            iterations = maxPeriod - 1,
        )
    }

    private fun optimizeEWMAAlpha(data: List<Double>): OptimizationResult {
        var bestAlpha = 0.3
        var bestError = Double.MAX_VALUE
        val iterations = 20

        for (i in 1..iterations) {
            val alpha = i / iterations.toDouble()
            val algorithm = ExponentialWeightedMovingAverage(alpha)

            val trainingSize = (data.size * 0.8).toInt()
            val trainingData = data.take(trainingSize)
            val testData = data.drop(trainingSize)

            if (testData.isEmpty()) continue

            val result = algorithm.forecast(trainingData, testData.size)
            val errors = result.forecastValues.zip(testData).map { (pred, actual) -> abs(pred - actual) }
            val mae = errors.average()

            if (mae < bestError) {
                bestError = mae
                bestAlpha = alpha
            }
        }

        return OptimizationResult(
            parameters = mapOf("alpha" to bestAlpha),
            confidence = 1.0 / (1.0 + bestError),
            iterations = iterations,
        )
    }

    private fun optimizeExponentialSmoothingAlpha(data: List<Double>): OptimizationResult {
        return optimizeEWMAAlpha(data) // Same optimization logic
    }

}

/**
 * Configuration for forecasting service
 */
data class ForecastingServiceConfig(
    val defaultAlgorithm: String = "SimpleExponentialSmoothing",
    val maxForecastPeriods: Int = 50,
    val enableParameterOptimization: Boolean = false,
)

/**
 * Algorithm recommendation with reasoning
 */
data class AlgorithmRecommendation(
    val algorithmName: String,
    val confidence: Double,
    val reason: String,
)

/**
 * Benchmark result for algorithm comparison
 */
data class AlgorithmBenchmark(
    val algorithmName: String,
    val meanAbsoluteError: Double,
    val meanAbsolutePercentageError: Double,
    val confidence: Double,
)

/**
 * Parameter optimization result
 */
data class OptimizationResult(
    val parameters: Map<String, Any>,
    val confidence: Double,
    val iterations: Int,
)
